import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
} from "react";
import { usePost } from "./usePost";
import { useToast } from "../store/ToastProvider";
import { Filter as Option } from "../types/PageFilterTypes";
import { ManagerItem } from "../types/CommonDataRequestTypes";
import { EngagementType } from "../models/stewardshipModels";
import i18n from "i18n-iso-countries";
import english from "i18n-iso-countries/langs/en.json";
import { formatReadable } from "../utils/formattingUtils";
import { toMetaTheme } from "../utils/engagementThemeUtils";
import {
  LinkageDashboardFilters,
  ThemeDynamicsRow,
  ThemeDynamicsBarDatum,
  ThemeDynamicsSegmentKey,
  MaterialEscalationRow,
  HeatmapSeries,
  CollaborationTensionRow,
  CollaborationScatterSeries,
  InfluenceDirection,
  LinkageFilterOptionsResponse,
  LinkageGicsRow,
} from "../types/linkageDashboardTypes";

i18n.registerLocale(english);

// =============================================================================
// DATA TRANSFORMATIONS
// =============================================================================

const INFLUENCE_DIRECTIONS: InfluenceDirection[] = [
  "engagement_drives_vote",
  "vote_triggers_engagement",
  "bidirectional",
  "unknown",
];

const META_THEME_ORDER: Record<string, number> = {
  [EngagementType.Environmental]: 0,
  [EngagementType.Social]: 1,
  [EngagementType.Governance]: 2,
  [EngagementType.StrategyRiskCommunication]: 3,
};

const resolveMetaTheme = (theme: string, rowMetaTheme?: string): string =>
  rowMetaTheme || toMetaTheme(theme);

export const pivotThemeDynamics = (
  rows: ThemeDynamicsRow[],
): ThemeDynamicsBarDatum[] => {
  const themeMap = new Map<string, ThemeDynamicsBarDatum>();

  for (const row of rows) {
    if (!themeMap.has(row.theme)) {
      themeMap.set(row.theme, {
        theme: row.theme,
        meta_theme: resolveMetaTheme(row.theme, row.meta_theme),
        positive_alignment__same_manager: 0,
        positive_alignment__cross_manager: 0,
        negative_tension__same_manager: 0,
        negative_tension__cross_manager: 0,
        other__same_manager: 0,
        other__cross_manager: 0,
      });
    }
    const entry = themeMap.get(row.theme);
    if (!entry) continue;
    if (row.meta_theme && entry.meta_theme === "Strategy") {
      entry.meta_theme = row.meta_theme;
    }
    const key =
      `${row.alignment_category}__${row.manager_relationship}` as ThemeDynamicsSegmentKey;
    entry[key] = row.pct_within_theme;
  }

  return Array.from(themeMap.values()).sort((a, b) => {
    const orderA = META_THEME_ORDER[a.meta_theme] ?? 99;
    const orderB = META_THEME_ORDER[b.meta_theme] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return a.theme.localeCompare(b.theme);
  });
};

export const pivotMaterialEscalation = (
  rows: MaterialEscalationRow[],
  usePercentage: boolean,
): HeatmapSeries[] => {
  const topicMap = new Map<string, Map<string, number>>();

  for (const row of rows) {
    if (!topicMap.has(row.topic_name)) {
      topicMap.set(row.topic_name, new Map());
    }
    const dirMap = topicMap.get(row.topic_name);
    if (dirMap) {
      dirMap.set(
        row.influence_direction,
        usePercentage ? row.pct_within_topic : row.count,
      );
    }
  }

  return Array.from(topicMap.entries()).map(([topicName, directions]) => ({
    id: topicName,
    data: INFLUENCE_DIRECTIONS.filter((dir) => directions.has(dir)).map(
      (dir) => ({
        x: dir,
        y: directions.get(dir) || 0,
      }),
    ),
  }));
};

export const transformCollaborationTension = (
  rows: CollaborationTensionRow[],
): CollaborationScatterSeries[] => [
  {
    id: "themes",
    data: rows.map((row) => ({
      x: row.cross_manager_rate,
      y: row.tension_rate,
      theme: row.theme,
      size: row.total_linkages,
    })),
  },
];

// =============================================================================
// HOOK
// =============================================================================

export interface UseLinkageDashboardReturn {
  // Filter options
  managerOptions: Option[];
  companyOptions: Option[];
  countryOptions: Option[];
  gicsSectorOptions: Option[];
  selectedCountries: Option[];
  selectedGicsSector: Option[];
  // Filter state setters
  setSelectedManagers: (options: Option[]) => void;
  setSelectedCompany: (options: Option[]) => void;
  setSelectedCountries: (options: Option[]) => void;
  setSelectedGicsSector: (options: Option[]) => void;
  // Chart data
  themeDynamicsData: ThemeDynamicsBarDatum[];
  materialEscalationData: HeatmapSeries[];
  collaborationTensionData: CollaborationScatterSeries[];
  // Raw data for tooltips
  materialEscalationRaw: MaterialEscalationRow[];
  collaborationTensionRaw: CollaborationTensionRow[];
  // Loading states
  isLoadingOptions: boolean;
  isLoadingThemeDynamics: boolean;
  isLoadingMaterialEscalation: boolean;
  isLoadingCollaborationTension: boolean;
  // Toggle
  heatmapShowPercentage: boolean;
  setHeatmapShowPercentage: (v: boolean) => void;
  // Enum filters (shared across charts + table)
  enumFilters: Record<string, string[]>;
  setEnumFilters: Dispatch<SetStateAction<Record<string, string[]>>>;
  // Filter payload for table
  filterPayload: LinkageDashboardFilters;
}

export const useLinkageDashboard = (): UseLinkageDashboardReturn => {
  const { addToast } = useToast();

  // ========== Filter Options State ==========
  const [managerOptions, setManagerOptions] = useState<Option[]>([]);
  const [companyOptions, setCompanyOptions] = useState<Option[]>([]);
  const [countryOptions, setCountryOptions] = useState<Option[]>([]);
  const [allGicsRows, setAllGicsRows] = useState<LinkageGicsRow[]>([]);

  // ========== Filter Selections ==========
  const [selectedManagers, setSelectedManagers] = useState<Option[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Option[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<Option[]>([]);
  const [selectedGicsSector, setSelectedGicsSector] = useState<Option[]>([]);

  // ========== Enum column filters (shared across charts + table) ==========
  const [enumFilters, setEnumFilters] = useState<Record<string, string[]>>({});

  // ========== Heatmap toggle ==========
  const [heatmapShowPercentage, setHeatmapShowPercentage] =
    useState<boolean>(true);

  // ========== Chart Raw Data ==========
  const [themeDynamicsRaw, setThemeDynamicsRaw] = useState<ThemeDynamicsRow[]>(
    [],
  );
  const [materialEscalationRaw, setMaterialEscalationRaw] = useState<
    MaterialEscalationRow[]
  >([]);
  const [collaborationTensionRaw, setCollaborationTensionRaw] = useState<
    CollaborationTensionRow[]
  >([]);

  // ========== API Hooks ==========
  const { sendData: fetchManagers, isLoading: isManagersLoading } = usePost<
    ManagerItem[],
    null
  >();
  const { sendData: fetchCompanies, isLoading: isCompaniesLoading } = usePost<
    { compass_cid: string; name: string }[],
    null
  >();
  const { sendData: fetchFilterOptions, isLoading: isFilterOptionsLoading } =
    usePost<LinkageFilterOptionsResponse, Record<string, never>>();

  const { sendData: fetchThemeDynamics, isLoading: isLoadingThemeDynamics } =
    usePost<ThemeDynamicsRow[], LinkageDashboardFilters>();

  const {
    sendData: fetchMaterialEscalation,
    isLoading: isLoadingMaterialEscalation,
  } = usePost<MaterialEscalationRow[], LinkageDashboardFilters>();

  const {
    sendData: fetchCollaborationTension,
    isLoading: isLoadingCollaborationTension,
  } = usePost<CollaborationTensionRow[], LinkageDashboardFilters>();

  // ========== Derived filter payload ==========
  const filterPayload = useMemo<LinkageDashboardFilters>(
    () => ({
      compass_cid:
        selectedCompany.length > 0
          ? selectedCompany.map((c) => c.key as string)
          : null,
      manager_id:
        selectedManagers.length > 0
          ? selectedManagers.map((m) => m.key as string)
          : null,
      country_iso2:
        selectedCountries.length > 0
          ? selectedCountries.map((c) => c.key as string)
          : null,
      gics_sector:
        selectedGicsSector.length > 0
          ? selectedGicsSector.map((s) => Number(s.key))
          : null,
      alignment_category: enumFilters.alignment?.length
        ? enumFilters.alignment
            .map(
              (v) =>
                (
                  ({
                    CONSISTENT_REINFORCEMENT: "positive_alignment",
                    SYNERGISTIC_ALIGNMENT: "positive_alignment",
                    INTERNAL_INCONSISTENCY: "negative_tension",
                    ACTIVE_UNDERMINING: "negative_tension",
                  }) as Record<string, string>
                )[v] || v,
            )
            .filter((v, i, a) => a.indexOf(v) === i)
        : null,
      manager_relationship: enumFilters.relationship?.length
        ? enumFilters.relationship
        : null,
      influence_direction: enumFilters.direction?.length
        ? enumFilters.direction
            .map(
              (v) =>
                (
                  ({
                    ENGAGEMENT_PRECEDES_VOTE: "engagement_drives_vote",
                    VOTE_PRECEDES_ENGAGEMENT: "vote_triggers_engagement",
                    BIDIRECTIONAL: "bidirectional",
                  }) as Record<string, string>
                )[v] || v,
            )
            .filter((v, i, a) => a.indexOf(v) === i)
        : null,
      linkage_type: enumFilters.linkageType?.length
        ? enumFilters.linkageType
        : null,
    }),
    [
      selectedCompany,
      selectedManagers,
      selectedCountries,
      selectedGicsSector,
      enumFilters,
    ],
  );

  // ========== GICS sector options ==========
  const gicsSectorOptions = useMemo<Option[]>(() => {
    const seen = new Map<number, string>();
    for (const r of allGicsRows) {
      if (!seen.has(r.gics_sector)) seen.set(r.gics_sector, r.gics_sector_name);
    }
    return Array.from(seen, ([code, name]) => ({
      key: String(code),
      displayValue: name,
    })).sort((a, b) => a.displayValue.localeCompare(b.displayValue));
  }, [allGicsRows]);

  // ========== Transformed chart data ==========
  const themeDynamicsData = useMemo(
    () => pivotThemeDynamics(themeDynamicsRaw),
    [themeDynamicsRaw],
  );

  const materialEscalationData = useMemo(
    () => pivotMaterialEscalation(materialEscalationRaw, heatmapShowPercentage),
    [materialEscalationRaw, heatmapShowPercentage],
  );

  const collaborationTensionData = useMemo(
    () => transformCollaborationTension(collaborationTensionRaw),
    [collaborationTensionRaw],
  );

  // ========== Fetch filter options on mount ==========
  useEffect(() => {
    // Each call is independent so one failure doesn't break the others
    const loadManagers = async (): Promise<void> => {
      try {
        const data = await fetchManagers("GET_ALL_MANAGERS", null);
        if (data) {
          setManagerOptions(
            data.map((m) => ({
              key: m.manager_id,
              displayValue: m.manager_name,
            })),
          );
        }
      } catch {
        addToast("Failed to load managers", "error");
      }
    };

    const loadCompanies = async (): Promise<void> => {
      try {
        const data = await fetchCompanies("GET_COMPANIES", null);
        if (data) {
          setCompanyOptions(
            data.map((c) => ({
              key: c.compass_cid,
              displayValue: formatReadable(c.name),
            })),
          );
        }
      } catch {
        addToast("Failed to load companies", "error");
      }
    };

    const loadFilterOptions = async (): Promise<void> => {
      try {
        const data = await fetchFilterOptions("GET_LINKAGE_FILTER_OPTIONS", {});
        if (data) {
          if (data.countries) {
            setCountryOptions(
              data.countries
                .map((c) => ({
                  key: c.country_iso2,
                  displayValue:
                    i18n.getName(c.country_iso2.toUpperCase(), "en") ||
                    c.country_name ||
                    c.country_iso2,
                }))
                .sort((a, b) => a.displayValue.localeCompare(b.displayValue)),
            );
          }
          setAllGicsRows(data.gics || []);
        }
      } catch {
        addToast("Failed to load filter options", "error");
      }
    };

    loadManagers();
    loadCompanies();
    loadFilterOptions();
  }, []);

  // ========== Fetch chart data when filters change ==========
  const fetchAllChartData = useCallback(
    async (payload: LinkageDashboardFilters): Promise<void> => {
      try {
        const [tdData, materialData, collaborationData] = await Promise.all([
          fetchThemeDynamics("GET_LINKAGE_THEME_DYNAMICS", payload),
          fetchMaterialEscalation("GET_LINKAGE_MATERIAL_ESCALATION", payload),
          fetchCollaborationTension(
            "GET_LINKAGE_COLLABORATION_TENSION",
            payload,
          ),
        ]);

        setThemeDynamicsRaw(tdData || []);
        if (materialData) setMaterialEscalationRaw(materialData);
        if (collaborationData) setCollaborationTensionRaw(collaborationData);
      } catch {
        addToast("Failed to fetch linkage dashboard data", "error");
      }
    },
    [],
  );

  useEffect(() => {
    fetchAllChartData(filterPayload);
  }, [filterPayload, fetchAllChartData]);

  // ========== Filter handlers ==========
  const handleSetManagers = useCallback((options: Option[]) => {
    setSelectedManagers(options);
  }, []);

  const handleSetCompany = useCallback((options: Option[]) => {
    setSelectedCompany(options);
  }, []);

  const handleSetCountries = useCallback((options: Option[]) => {
    setSelectedCountries(options);
  }, []);

  const handleSetGicsSector = useCallback((options: Option[]) => {
    setSelectedGicsSector(options);
  }, []);

  return {
    // Filter options
    managerOptions,
    companyOptions,
    countryOptions,
    gicsSectorOptions,
    selectedCountries,
    selectedGicsSector,
    // Filter setters
    setSelectedManagers: handleSetManagers,
    setSelectedCompany: handleSetCompany,
    setSelectedCountries: handleSetCountries,
    setSelectedGicsSector: handleSetGicsSector,
    // Chart data
    themeDynamicsData,
    materialEscalationData,
    collaborationTensionData,
    // Raw data
    materialEscalationRaw,
    collaborationTensionRaw,
    // Loading states
    isLoadingOptions:
      isManagersLoading || isCompaniesLoading || isFilterOptionsLoading,
    isLoadingThemeDynamics,
    isLoadingMaterialEscalation,
    isLoadingCollaborationTension,
    // Toggle
    heatmapShowPercentage,
    setHeatmapShowPercentage,
    // Enum filters (lifted for charts + table)
    enumFilters,
    setEnumFilters,
    // Filter payload for table
    filterPayload,
  };
};
